<script lang="ts">
    import type InstagramPostEntity from "@apps/instagram/entities/InstagramPostEntity";
    import { combineLatest } from "rxjs";
    import { filter, switchMap, tap } from "rxjs/operators";
    import { createEventDispatcher, onMount } from "svelte";

    import { ModalToken } from "@components/Modal/ModalManager";
    import Slider from "@components/Slider/Slider.svelte";
    import SliderArrows from "@components/Slider/SliderArrows.svelte";
    import SliderController from "@components/Slider/SliderController";

    import InstagramPost from "../components/InstagramPost.svelte";
    import InstagramPostSkeleton from "../components/InstagramPostSkeleton.svelte";
    import {
        DISPLAY_MODE_HOTSPOT_TYPE,
        DISPLAY_MODE_SHOP_IT_TYPE,
    } from "../constants";

    export let posts: InstagramPostEntity[];
    export let postModalToken: ModalToken<{ id: number }>;
    export let perPage: number;
    export let mobilePerPage: number;
    export let displayMode:
        | typeof DISPLAY_MODE_SHOP_IT_TYPE
        | typeof DISPLAY_MODE_HOTSPOT_TYPE
        | null;
    export let skeleton = false;
    export let skeletonPostsCount = 8;
    export let loop = false;
    export let gutter = 0;
    export let highQualityEnabled: boolean;
    export let postsTotalCount: number;
    let hasNext = false;
    let hasPrev = false;
    const dispatch = createEventDispatcher();
    const sliderController = new SliderController();
    const currentIndex$ = sliderController.connected$.pipe(
        filter((v) => !!v),
        switchMap(() => {
            return sliderController.selectCurrentSlideIndex();
        })
    );
    let slidesCount$ = sliderController.connected$.pipe(
        filter((v) => !!v),
        switchMap(() => {
            return sliderController.selectSlidesCount();
        })
    );
    let currentPerPage$ = sliderController.connected$.pipe(
        filter((v) => !!v),
        switchMap(() => {
            return sliderController.selectCurrentPerPage();
        })
    );
    const handleNext = (): void => {
        sliderController.next();
    };
    const handlePrev = (): void => {
        sliderController.prev();
    };
    onMount(() => {
        const currentSubscribe = currentIndex$.subscribe((index) => {
            if (index === posts.length - $currentPerPage$) dispatch("loadMore");
        });
        const arrowSubscribe = combineLatest([currentIndex$, currentPerPage$])
            .pipe(
                tap(([currentIndex, currentPerPage]) => {
                    if (currentIndex === postsTotalCount - currentPerPage) {
                        hasNext = false;
                        hasPrev = true;
                    } else if (currentIndex === 0) {
                        hasNext = true;
                        hasPrev = false;
                    } else {
                        hasNext = true;
                        hasPrev = true;
                    }
                })
            )
            .subscribe();
        return () => {
            arrowSubscribe.unsubscribe();
            currentSubscribe.unsubscribe();
        };
    });
</script>

{#key posts}
    <div
        class="gw-ig-gallery-slide-layout"
        style={`--gw-slider-gutter: ${gutter}px`}
    >
        <Slider
            {sliderController}
            slidesCount={$slidesCount$}
            {loop}
            perPage={{
                0: mobilePerPage,
                450: perPage,
            }}
            duration={600}
        >
            <svelte:fragment slot="slides">
                {#if skeleton}
                    {#each { length: skeletonPostsCount } as _}
                        <div
                            class="gw-ig-gallery-slide-layout__post-wrapper gw-ig-gallery-slide-layout__post-wrapper--skeleton"
                        >
                            <InstagramPostSkeleton square={true} />
                        </div>
                    {/each}
                {:else}
                    {#each posts as post}
                        <div class="gw-ig-gallery-slide-layout__post-wrapper">
                            <InstagramPost
                                {post}
                                square={true}
                                {postModalToken}
                                imageObjectFit="cover"
                                {displayMode}
                                {highQualityEnabled}
                            />
                        </div>
                    {/each}
                {/if}
            </svelte:fragment>
        </Slider>
        {#if $slidesCount$ > $currentPerPage$}
            <SliderArrows
                on:nextSlide={handleNext}
                on:prevSlide={handlePrev}
                {hasNext}
                {hasPrev}
            />
        {/if}
    </div>
{/key}

<style>
    .gw-ig-gallery-slide-layout {
        position: relative;
        width: 100%;
        margin: 0 auto;
    }

    .gw-ig-gallery-slide-layout__post-wrapper {
        margin: 0 var(--gw-slider-gutter);
    }
</style>
